import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { Users, Heart, Book, Stethoscope } from 'lucide-react';

const Volunteer = () => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await axios.post(`${import.meta.env.VITE_API_URL}/volunteers`, data);
      toast.success('Registration successful! We will contact you soon.');
      reset();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Heart, title: 'Make Impact', desc: 'Directly improve lives' },
    { icon: Users, title: 'Build Network', desc: 'Connect with changemakers' },
    { icon: Book, title: 'Learn Skills', desc: 'Gain valuable experience' },
    { icon: Stethoscope, title: 'Get Recognition', desc: 'Receive certificates' }
  ];

  return (
    <div className="section-padding bg-gray-50">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Join Our Mission</h1>
            <p className="text-xl text-gray-600">
              Be part of a community dedicated to positive change
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-4 mb-12">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card p-4 text-center"
              >
                <benefit.icon className="w-8 h-8 text-primary mx-auto mb-2" />
                <h3 className="font-bold mb-1">{benefit.title}</h3>
                <p className="text-sm text-gray-600">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="card p-8"
          >
            <h2 className="text-2xl font-bold mb-6">Volunteer Registration</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-semibold mb-2">Full Name *</label>
                  <input 
                    {...register('name', { required: 'Name is required' })} 
                    className="input" 
                    placeholder="John Doe"
                  />
                  {errors.name && <span className="text-red-500 text-sm">{errors.name.message}</span>}
                </div>
                
                <div>
                  <label className="block font-semibold mb-2">Email *</label>
                  <input 
                    type="email" 
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })} 
                    className="input" 
                    placeholder="john@example.com"
                  />
                  {errors.email && <span className="text-red-500 text-sm">{errors.email.message}</span>}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-semibold mb-2">Phone Number *</label>
                  <input 
                    {...register('phone', { 
                      required: 'Phone is required',
                      pattern: {
                        value: /^[0-9]{10}$/,
                        message: '10-digit phone number required'
                      }
                    })} 
                    className="input" 
                    placeholder="9876543210"
                  />
                  {errors.phone && <span className="text-red-500 text-sm">{errors.phone.message}</span>}
                </div>
                
                <div>
                  <label className="block font-semibold mb-2">City *</label>
                  <input 
                    {...register('city', { required: 'City is required' })} 
                    className="input" 
                    placeholder="Mumbai"
                  />
                  {errors.city && <span className="text-red-500 text-sm">{errors.city.message}</span>}
                </div>
              </div>
              
              <div>
                <label className="block font-semibold mb-2">Areas of Interest * (Select at least one)</label>
                <div className="grid md:grid-cols-2 gap-3">
                  {['Education', 'Healthcare', 'Environment', 'Elderly Care'].map(interest => (
                    <label key={interest} className="flex items-center p-3 border-2 border-gray-200 rounded-lg hover:border-primary cursor-pointer">
                      <input 
                        type="checkbox" 
                        value={interest} 
                        {...register('interests', { required: 'Select at least one interest' })} 
                        className="mr-3 w-4 h-4 text-primary"
                      />
                      <span>{interest}</span>
                    </label>
                  ))}
                </div>
                {errors.interests && <span className="text-red-500 text-sm">{errors.interests.message}</span>}
              </div>
              
              <div>
                <label className="block font-semibold mb-2">Availability</label>
                <select {...register('availability')} className="input">
                  <option value="Weekends Only">Weekends Only</option>
                  <option value="Weekdays">Weekdays</option>
                  <option value="Both Weekdays & Weekends">Both Weekdays & Weekends</option>
                  <option value="Flexible">Flexible</option>
                </select>
              </div>
              
              <div>
                <label className="block font-semibold mb-2">Why do you want to volunteer?</label>
                <textarea 
                  {...register('message')} 
                  className="input" 
                  rows="4"
                  placeholder="Tell us about your motivation..."
                ></textarea>
              </div>
              
              <button 
                type="submit" 
                disabled={loading} 
                className="btn btn-primary w-full text-lg"
              >
                {loading ? 'Submitting...' : 'Register as Volunteer'}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Volunteer;
